import React, { useEffect, useState } from "react";
import { UseFormReturn, FieldValues } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/atoms/Form";
import Select from "@/components/molecules/Select";
import Input from "@/components/atoms/Input";

type InternalTicketType = {
  id: number;
  name: string;
  children: { id: number; name: string }[];
};

type InternalTicketTypeRow = {
  id?: number;
  internal_ticket_type_id: string;
  internal_sub_ticket_type_id: string;
  internal_sub_ticket_type_count: number;
};

interface InternalTicketTypeSectionProps {
  form: UseFormReturn<FieldValues>;
  internalTicketTypes: InternalTicketType[];
  edit: boolean;
  existingRows?: {
    id: number;
    internal_ticket_type: { id: number; name: string };
    internal_sub_ticket_type: { id: number; name: string };
    internal_sub_ticket_type_count: number;
  }[];
}

const InternalTicketTypeSection: React.FC<InternalTicketTypeSectionProps> = ({
  form,
  internalTicketTypes,
  edit,
  existingRows = [],
}) => {
  const [rows, setRows] = useState<InternalTicketTypeRow[]>([
    { internal_ticket_type_id: "", internal_sub_ticket_type_id: "", internal_sub_ticket_type_count: 0 },
  ]);

  useEffect(() => {
    if (edit && existingRows.length > 0) {
      const mappedRows = existingRows.map((row) => ({
        id: row.id,
        internal_ticket_type_id: row.internal_ticket_type.id.toString(),
        internal_sub_ticket_type_id: row.internal_sub_ticket_type.id.toString(),
        internal_sub_ticket_type_count: row.internal_sub_ticket_type_count,
      }));

      setRows(mappedRows);

      // Initialize form values
      mappedRows.forEach((row, index) => {
        form.setValue(`tickets_internal_ticket_types_attributes[${index}].id`, row.id);
        form.setValue(
          `tickets_internal_ticket_types_attributes[${index}].internal_ticket_type_id`,
          row.internal_ticket_type_id
        );
        form.setValue(
          `tickets_internal_ticket_types_attributes[${index}].internal_sub_ticket_type_id`,
          row.internal_sub_ticket_type_id
        );
        form.setValue(
          `tickets_internal_ticket_types_attributes[${index}].internal_sub_ticket_type_count`,
          parseFloat(row.internal_sub_ticket_type_count.toString())
        );
      });
    }
  }, [edit, existingRows, form]);

  const handleAddRow = () => {
    setRows([
      ...rows,
      { internal_ticket_type_id: "", internal_sub_ticket_type_id: "", internal_sub_ticket_type_count: 0 },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index));
    form.setValue(`tickets_internal_ticket_types_attributes[${index}]._destroy`, true);
  };

  const handleFieldChange = <T extends keyof InternalTicketTypeRow>(
    index: number,
    field: T,
    value: InternalTicketTypeRow[T]
  ) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  
    // Update form values
    form.setValue(
      `tickets_internal_ticket_types_attributes[${index}].${field}` as any,
      value
    );
  
    if (field === "internal_ticket_type_id") {
      const selectedType = internalTicketTypes.find(
        (type) => type.id === parseInt(value as string)
      );
      form.setValue(
        `tickets_internal_ticket_types_attributes[${index}].internal_sub_ticket_type_id`,
        ""
      );
      updatedRows[index].internal_sub_ticket_type_id = ""; // Reset dependent field
      setRows(updatedRows);
    }
  };
  

  return (
    <div className="w-full flex flex-col gap-6 pb-4">
      <div className="w-full">

      
      {rows.map((row, index) => (
        <div
          key={row.id || index}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-6 border-b border-border mb-4"
        >
          {/* Internal Ticket Type */}
          <FormField
            control={form.control}
            name={`tickets_internal_ticket_types_attributes[${index}].internal_ticket_type_id`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Internal Ticket Type</FormLabel>
                <FormControl>
                  <Select
                    options={internalTicketTypes.map((type) => ({
                      label: type.name,
                      value: type.id.toString(),
                    }))}
                    value={row.internal_ticket_type_id}
                    onChange={(value) =>
                      handleFieldChange(index, "internal_ticket_type_id", value)
                    }
                    placeholder="Select"
                    enabledSearch={true}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Internal Sub Ticket Type */}
          <FormField
            control={form.control}
            name={`tickets_internal_ticket_types_attributes[${index}].internal_sub_ticket_type_id`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Internal Sub Ticket Type</FormLabel>
                <FormControl>
                  <Select
                    options={
                      internalTicketTypes
                        .find((type) => type.id === parseInt(row.internal_ticket_type_id))
                        ?.children.map((subType) => ({
                          label: subType.name,
                          value: subType.id.toString(),
                        })) || []
                    }
                    value={row.internal_sub_ticket_type_id}
                    onChange={(value) =>
                      handleFieldChange(index, "internal_sub_ticket_type_id", value)
                    }
                    placeholder="Select"
                    enabledSearch={true}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Sub Ticket Type Count */}
          <FormField
            control={form.control}
            name={`tickets_internal_ticket_types_attributes[${index}].internal_sub_ticket_type_count`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sub Ticket Type Count</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    value={row.internal_sub_ticket_type_count}
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        "internal_sub_ticket_type_count",
                        parseInt(e.target.value)
                      )
                    }
                    placeholder="0"
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Remove Row */}
          {rows.length > 1 && edit && (
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => handleRemoveRow(index)}
                className="text-red-600 text-sm"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      ))}

      {/* Add Row Button */}
      {edit && (
        <button
          type="button"
          onClick={handleAddRow}
          className="text-blue-600 text-sm mt-4"
        >
          + Add another internal ticket type
        </button>
      )}
      </div>
    </div>
  );
};

export default InternalTicketTypeSection;
